import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftRight,
  Loader2,
  ArrowDownLeft,
  ArrowUpRight,
  Package,
  RotateCcw,
  Trash2,
  Plus,
  Minus,
  X,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { ProductFilterBar } from '@/components/trading/product-filter-bar';

const movementTypeConfig: Record<string, { icon: any; color: string }> = {
  PURCHASE_RECEIPT: { icon: ArrowDownLeft, color: 'text-green-600 bg-green-100' },
  SALES_ISSUE: { icon: ArrowUpRight, color: 'text-red-600 bg-red-100' },
  ADJUSTMENT_IN: { icon: Plus, color: 'text-blue-600 bg-blue-100' },
  ADJUSTMENT_OUT: { icon: Minus, color: 'text-orange-600 bg-orange-100' },
  TRANSFER_IN: { icon: ArrowDownLeft, color: 'text-purple-600 bg-purple-100' },
  TRANSFER_OUT: { icon: ArrowUpRight, color: 'text-purple-600 bg-purple-100' },
  RETURN_FROM_CUSTOMER: { icon: RotateCcw, color: 'text-cyan-600 bg-cyan-100' },
  RETURN_TO_SUPPLIER: { icon: RotateCcw, color: 'text-amber-600 bg-amber-100' },
  SCRAP: { icon: Trash2, color: 'text-gray-600 bg-gray-100' },
  INITIAL_STOCK: { icon: Package, color: 'text-indigo-600 bg-indigo-100' },
  CONSIGNMENT_OUT: { icon: ArrowUpRight, color: 'text-teal-600 bg-teal-100' },
  CONSIGNMENT_RETURN: { icon: RotateCcw, color: 'text-teal-600 bg-teal-100' },
  CONSIGNMENT_SOLD: { icon: ArrowUpRight, color: 'text-emerald-600 bg-emerald-100' },
};

export function StockMovementsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [brandId, setBrandId] = useState<string | undefined>();
  const [modelId, setModelId] = useState<string | undefined>();
  const [warehouseId, setWarehouseId] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [page, setPage] = useState(1);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  };

  const handleBrandChange = (value: string | undefined) => {
    setBrandId(value);
    setModelId(undefined);
    setPage(1);
  };

  const handleModelChange = (value: string | undefined) => {
    setModelId(value);
    setPage(1);
  };

  const handleWarehouseChange = (value: string) => {
    setWarehouseId(value);
    setPage(1);
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setPage(1);
  };

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    setPage(1);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    setPage(1);
  };

  const hasActiveFilters =
    !!debouncedSearch ||
    !!brandId ||
    !!modelId ||
    warehouseId !== 'all' ||
    !!dateFrom ||
    !!dateTo ||
    selectedType !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setBrandId(undefined);
    setModelId(undefined);
    setWarehouseId('all');
    setDateFrom('');
    setDateTo('');
    setSelectedType('all');
    setPage(1);
  };

  const { data: warehousesData } = trpc.warehouse.list.useQuery({});
  const warehouses = warehousesData ?? [];

  const { data, isLoading, error } = trpc.stockMovement.list.useQuery({
    search: debouncedSearch || undefined,
    brandId,
    modelId,
    warehouseId: warehouseId !== 'all' ? warehouseId : undefined,
    dateFrom: dateFrom ? new Date(dateFrom) : undefined,
    dateTo: dateTo ? new Date(dateTo) : undefined,
    type: selectedType !== 'all' ? selectedType as 'PURCHASE_RECEIPT' | 'SALES_ISSUE' | 'ADJUSTMENT_IN' | 'ADJUSTMENT_OUT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'RETURN_FROM_CUSTOMER' | 'RETURN_TO_SUPPLIER' | 'SCRAP' | 'INITIAL_STOCK' : undefined,
    page,
  });

  const movements = data?.movements ?? [];
  const pagination = data?.pagination;

  const getMovementTypeLabel = (type: string) => {
    switch (type) {
      case 'PURCHASE_RECEIPT':
        return t('inventory.movements.purchaseReceipt');
      case 'SALES_ISSUE':
        return t('inventory.movements.salesIssue');
      case 'ADJUSTMENT_IN':
        return t('inventory.movements.adjustmentIn');
      case 'ADJUSTMENT_OUT':
        return t('inventory.movements.adjustmentOut');
      case 'TRANSFER_IN':
        return t('inventory.movements.transferIn');
      case 'TRANSFER_OUT':
        return t('inventory.movements.transferOut');
      case 'RETURN_FROM_CUSTOMER':
        return t('inventory.movements.returnFromCustomer');
      case 'RETURN_TO_SUPPLIER':
        return t('inventory.movements.returnToSupplier');
      case 'SCRAP':
        return t('inventory.movements.scrap');
      case 'INITIAL_STOCK':
        return t('inventory.movements.initialStock');
      case 'CONSIGNMENT_OUT':
        return t('inventory.movements.consignmentOut', 'Konsinye Çıkış');
      case 'CONSIGNMENT_RETURN':
        return t('inventory.movements.consignmentReturn', 'Konsinye İade');
      case 'CONSIGNMENT_SOLD':
        return t('inventory.movements.consignmentSold', 'Konsinye Satış');
      default:
        return type;
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    // StockMovement.unitCost/totalCost are TRY book values (frozen at receipt).
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const movementTypes = [
    'PURCHASE_RECEIPT',
    'SALES_ISSUE',
    'ADJUSTMENT_IN',
    'ADJUSTMENT_OUT',
    'TRANSFER_IN',
    'TRANSFER_OUT',
    'RETURN_FROM_CUSTOMER',
    'RETURN_TO_SUPPLIER',
    'SCRAP',
    'INITIAL_STOCK',
    'CONSIGNMENT_OUT',
    'CONSIGNMENT_RETURN',
    'CONSIGNMENT_SOLD',
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('inventory.movements.title')}</h1>
        <p className="text-muted-foreground">
          {t('inventory.movements.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <ProductFilterBar
          search={searchQuery}
          onSearchChange={handleSearchChange}
          brandId={brandId}
          onBrandChange={handleBrandChange}
          modelId={modelId}
          onModelChange={handleModelChange}
        />

        <Select value={warehouseId} onValueChange={handleWarehouseChange}>
          <SelectTrigger className="w-[200px]" aria-label={t('inventory.stockLevels.filterByWarehouse')}>
            <SelectValue placeholder={t('inventory.stockLevels.selectWarehouse')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.stockLevels.allWarehouses')}</SelectItem>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <input
          type="date"
          aria-label={t('inventory.movements.dateFrom', 'Başlangıç')}
          value={dateFrom}
          onChange={(e) => handleDateFromChange(e.target.value)}
          className="h-8 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
        />
        <input
          type="date"
          aria-label={t('inventory.movements.dateTo', 'Bitiş')}
          value={dateTo}
          onChange={(e) => handleDateToChange(e.target.value)}
          className="h-8 border border-border bg-background px-2 text-sm focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20"
        />

        <Select value={selectedType} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('inventory.movements.selectType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.movements.allTypes')}</SelectItem>
            {movementTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {getMovementTypeLabel(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1.5 h-8 border border-border bg-background px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          >
            <X className="h-3.5 w-3.5" />
            {t('common.clearFilters', 'Filtreleri Temizle')}
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && movements.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <ArrowLeftRight className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('inventory.movements.noMovements')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('inventory.movements.noMovementsDesc')}
          </p>
        </div>
      )}

      {/* Stock Movements Table */}
      {!isLoading && !error && movements.length > 0 && (
        <>
          <div className="border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.movementNumber')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.type')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.product')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.fromWarehouse')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.toWarehouse')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('inventory.movements.quantity')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('inventory.movements.unitCost')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('inventory.movements.totalCost')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.createdBy')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('inventory.movements.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movements.map((movement) => {
                  const config = movementTypeConfig[movement.type] ?? { icon: Package, color: 'text-gray-600 bg-gray-100' };
                  const Icon = config?.icon ?? Package;

                  return (
                    <tr key={movement.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm">{movement.movementNumber}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 ${config?.color?.split(' ')[1] ?? 'bg-gray-100'}`}>
                            <Icon className={`h-4 w-4 ${config?.color?.split(' ')[0] ?? 'text-gray-600'}`} />
                          </div>
                          <span className="text-sm">{getMovementTypeLabel(movement.type)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-sm">{movement.productName}</div>
                          <div className="text-xs text-muted-foreground">{movement.productCode}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{movement.fromWarehouseName || '-'}</td>
                      <td className="px-4 py-3 text-sm">{movement.toWarehouseName || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${
                          movement.type.includes('OUT') || movement.type === 'SALES_ISSUE' || movement.type === 'SCRAP'
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}>
                          {movement.type.includes('OUT') || movement.type === 'SALES_ISSUE' || movement.type === 'SCRAP'
                            ? `-${movement.quantity}`
                            : `+${movement.quantity}`
                          }
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm">{formatCurrency(movement.unitCost)}</td>
                      <td className="px-4 py-3 text-right text-sm">{formatCurrency(movement.totalCost)}</td>
                      <td className="px-4 py-3 text-sm">{movement.createdBy || '-'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(movement.movementDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {tCommon('showing')} {(pagination.page - 1) * pagination.limit + 1} -{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} {tCommon('of')}{' '}
                {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="h-8 border border-border bg-background px-3 text-sm disabled:opacity-50 hover:bg-muted focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
                >
                  {tCommon('previous')}
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === pagination.totalPages}
                  className="h-8 border border-border bg-background px-3 text-sm disabled:opacity-50 hover:bg-muted focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
                >
                  {tCommon('next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
